import {
  ScreenSpaceEventHandler,
  ScreenSpaceEventType,
  SceneTransforms,
  Cartesian2,
  Cartesian3,
  Cartographic,
  CallbackProperty,
  Color,
  PolylineDashMaterialProperty,
  Rectangle,
  Math as CesiumMath,
  Transforms,
  Matrix3,
  Matrix4,
  PolygonHierarchy,
} from "cesium";
import { createUniqueId } from "../Utility";
import { disableCameraControls, restoreCameraControls } from "../MapUtility";

export function enableRectangleDrawingWithUI(viewer, originalCameraControls, onSave) {
  // draw state
  let consumed = false;
  let isDrawing = false;
  let startPoint = null;
  let currentMouse = null;

  // rotation + UI state
  let overlayRoot = null;
  let postRenderFn = null;
  let rotating = false;
  let pointerId = null;
  let angleDeg = 0;
  let angleOffsetDeg = 0;
  let baseAngleRad = 0;
  let cameraLocked = false;
  let escHandler = null;
  let captureEl = null;

  // ids
  const FILL_ID = "rect-fill-ui";
  const OUTLINE_ID = "rect-outline-ui";
  const POLY_ID = "rect-rot-poly";

  const handler = new ScreenSpaceEventHandler(viewer.canvas);
  const originalDestroy = handler.destroy.bind(handler);

  // utils
  const rmEntity = (id) => {
    const e = viewer.entities.getById(id);
    if (e) viewer.entities.remove(e);
  };
  const rmAllEntities = () => [FILL_ID, OUTLINE_ID, POLY_ID].forEach(rmEntity);
  const detachPostRender = () => {
    if (postRenderFn) viewer.scene.postRender.removeEventListener(postRenderFn);
    postRenderFn = null;
  };
  const rmOverlay = () => {
    try {
      overlayRoot?.remove();
    } catch {}
    overlayRoot = null;
  };

  // rotate-session end
  const endRotateSession = () => {
    if (!rotating) return;
    rotating = false;
    angleOffsetDeg = angleDeg;
    try {
      if (captureEl && pointerId != null) captureEl.releasePointerCapture(pointerId);
    } catch {}
    pointerId = null;
    captureEl = null;
    if (escHandler) {
      document.removeEventListener("keydown", escHandler, true);
      escHandler = null;
    }
    if (cameraLocked) {
      restoreCameraControls(viewer, originalCameraControls);
      cameraLocked = false;
    }
    viewer.container.style.cursor = "default";
  };

  const cleanup = () => {
    endRotateSession();
    detachPostRender();
    rmOverlay();
    rmAllEntities();
    if (cameraLocked) {
      restoreCameraControls(viewer, originalCameraControls);
      cameraLocked = false;
    }
    handler.removeInputAction(ScreenSpaceEventType.LEFT_DOWN);
    handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
    handler.removeInputAction(ScreenSpaceEventType.LEFT_UP);
  };
  handler.destroy = () => {
    cleanup();
    originalDestroy();
  };

  // safe pick from window coords
  const pickCartesian = (winPos) => {
    if (!winPos) return;
    const s = viewer.scene;
    let res;
    if (s.pickPositionSupported) {
      try {
        res = s.pickPosition(winPos);
      } catch {}
    }
    if (!res) res = s.camera.pickEllipsoid(winPos, s.globe.ellipsoid);
    return res || undefined;
  };

  // start draw
  const onLeftDown = (click) => {
    if (consumed || !click?.position) return;
    currentMouse = click.position;
    const cartesian = pickCartesian(click.position);
    if (!cartesian) return;
    startPoint = cartesian;
    if (isDrawing) return;
    isDrawing = true;

    handler.removeInputAction(ScreenSpaceEventType.LEFT_DOWN); // single-use
    rmAllEntities();
    disableCameraControls(viewer, originalCameraControls);

    // live fill
    viewer.entities.add({
      id: FILL_ID,
      rectangle: {
        coordinates: new CallbackProperty(() => {
          if (!isDrawing || !startPoint || !currentMouse) return null;
          const end = pickCartesian(currentMouse);
          if (!end) return null;
          const c1 = Cartographic.fromCartesian(startPoint);
          const c2 = Cartographic.fromCartesian(end);
          let w = Math.min(c1.longitude, c2.longitude);
          let s = Math.min(c1.latitude, c2.latitude);
          let e = Math.max(c1.longitude, c2.longitude);
          let n = Math.max(c1.latitude, c2.latitude);
          const EPS = 1e-12;
          if (e - w < EPS) e = w + EPS;
          if (n - s < EPS) n = s + EPS;
          return Rectangle.fromRadians(w, s, e, n);
        }, false),
        material: Color.BLUE.withAlpha(0.08),
        outline: false,
      },
    });

    // live dashed outline
    viewer.entities.add({
      id: OUTLINE_ID,
      polyline: {
        positions: new CallbackProperty(() => {
          if (!isDrawing || !startPoint || !currentMouse) return [];
          const end = pickCartesian(currentMouse);
          if (!end) return [];
          const c1 = Cartographic.fromCartesian(startPoint);
          const c2 = Cartographic.fromCartesian(end);
          const w = Math.min(c1.longitude, c2.longitude);
          const s = Math.min(c1.latitude, c2.latitude);
          const e = Math.max(c1.longitude, c2.longitude);
          const n = Math.max(c1.latitude, c2.latitude);
          return Cartesian3.fromRadiansArray([w, s, e, s, e, n, w, n, w, s]);
        }, false),
        width: 1,
        material: new PolylineDashMaterialProperty({
          color: Color.RED,
          gapColor: Color.TRANSPARENT,
          dashLength: 12,
        }),
        clampToGround: false,
      },
    });
  };
  const onMouseMove = (movement) => {
    if (movement) currentMouse = movement.endPosition;
  };

  const onLeftUp = () => {
    if (!isDrawing || !startPoint || !currentMouse) {
      restoreCameraControls(viewer, originalCameraControls);
      isDrawing = false;
      return;
    }
    const end = pickCartesian(currentMouse);
    if (!end) {
      restoreCameraControls(viewer, originalCameraControls);
      isDrawing = false;
      return;
    }

    // bounds (deg)
    const c1 = Cartographic.fromCartesian(startPoint);
    const c2 = Cartographic.fromCartesian(end);
    let bounds = {
      west: CesiumMath.toDegrees(Math.min(c1.longitude, c2.longitude)),
      south: CesiumMath.toDegrees(Math.min(c1.latitude, c2.latitude)),
      east: CesiumMath.toDegrees(Math.max(c1.longitude, c2.longitude)),
      north: CesiumMath.toDegrees(Math.max(c1.latitude, c2.latitude)),
    };
    const EPSd = 1e-8;
    if (bounds.east - bounds.west < EPSd) bounds.east = bounds.west + EPSd;
    if (bounds.north - bounds.south < EPSd) bounds.north = bounds.south + EPSd;

    // freeze dashed outline positions
    const border = viewer.entities.getById(OUTLINE_ID);
    if (border) {
      border.polyline.positions = Cartesian3.fromDegreesArray([bounds.west, bounds.south, bounds.east, bounds.south, bounds.east, bounds.north, bounds.west, bounds.north, bounds.west, bounds.south]);
    }

    // base corners + center
    const baseCorners = [
      [bounds.west, bounds.south],
      [bounds.east, bounds.south],
      [bounds.east, bounds.north],
      [bounds.west, bounds.north],
      [bounds.west, bounds.south],
    ];
    const centerLL = [(bounds.west + bounds.east) / 2, (bounds.south + bounds.north) / 2];
    const cornersLL = baseCorners.map((p) => [...p]);

    // hidden polygon to drive rotation
    viewer.entities.add({
      id: POLY_ID,
      polygon: {
        hierarchy: new CallbackProperty(() => {
          const pos = Cartesian3.fromDegreesArray(cornersLL.flat());
          return new PolygonHierarchy(pos);
        }, false),
        material: Color.TRANSPARENT,
        outline: false,
        perPositionHeight: false,
      },
    });

    // remove preview fill
    rmEntity(FILL_ID);

    // keep dashed border in sync
    const syncBorder = () => {
      const e = viewer.entities.getById(OUTLINE_ID);
      if (!e) return;
      const flat = [cornersLL[0][0], cornersLL[0][1], cornersLL[1][0], cornersLL[1][1], cornersLL[2][0], cornersLL[2][1], cornersLL[3][0], cornersLL[3][1], cornersLL[0][0], cornersLL[0][1]];
      e.polyline.positions = Cartesian3.fromDegreesArray(flat);
    };

    // rotate corners around center in ENU
    const rotateCorners = (deg) => {
      const rad = CesiumMath.toRadians(deg);
      const centerCart = Cartesian3.fromDegrees(centerLL[0], centerLL[1]);
      const enu = Transforms.eastNorthUpToFixedFrame(centerCart);
      const enuInv = Matrix4.inverse(enu, new Matrix4());
      const Rz = Matrix3.fromRotationZ(rad);
      const Rz4 = Matrix4.fromRotationTranslation(Rz);

      const apply = (lon, lat) => {
        const p = Cartesian3.fromDegrees(lon, lat);
        const pLocal = Matrix4.multiplyByPoint(enuInv, p, new Cartesian3());
        const pRotLocal = Matrix4.multiplyByPoint(Rz4, pLocal, new Cartesian3());
        const pWorld = Matrix4.multiplyByPoint(enu, pRotLocal, new Cartesian3());
        const cg = Cartographic.fromCartesian(pWorld);
        return [CesiumMath.toDegrees(cg.longitude), CesiumMath.toDegrees(cg.latitude)];
      };

      const rotated = baseCorners.map(([lo, la]) => apply(lo, la));
      cornersLL.splice(0, cornersLL.length, ...rotated);
      syncBorder();
    };

    // helpers
    const toWinCartesian = (cart) => {
      const s = viewer.scene;
      const p = SceneTransforms?.wgs84ToWindowCoordinates ? SceneTransforms.wgs84ToWindowCoordinates(s, cart) : s.cartesianToCanvasCoordinates(cart);
      return p || null;
    };
    const toWin = (lon, lat) => toWinCartesian(Cartesian3.fromDegrees(lon, lat));
    const lerpLL = (a, b, t) => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];

    // estimate slippy zoom
    const metersPerPixelAt = (lon, lat) => {
      const center = Cartesian3.fromDegrees(lon, lat);
      const enu = Transforms.eastNorthUpToFixedFrame(center);
      const p1 = toWinCartesian(center);
      const p2World = Matrix4.multiplyByPoint(enu, new Cartesian3(1, 0, 0), new Cartesian3()); // +1m east
      const p2 = toWinCartesian(p2World);
      if (!p1 || !p2) return null;
      const dpx = Math.hypot(p2.x - p1.x, p2.y - p1.y);
      if (dpx <= 0) return null;
      return 1 / dpx;
    };
    const currentZoomAtCenter = () => {
      const mpp = metersPerPixelAt(centerLL[0], centerLL[1]);
      if (!mpp) return 15;
      const EQUATOR_MPP_Z0 = 156543.03392804097;
      const latRad = CesiumMath.toRadians(centerLL[1]);
      const z = Math.log2((EQUATOR_MPP_Z0 * Math.cos(latRad)) / mpp);
      if (!Number.isFinite(z)) return 0;
      const z2 = Math.round(z * 100) / 100; // 2 decimals
      return Math.max(0, Math.min(24, z2));
    };

    // overlay root
    overlayRoot = document.createElement("div");
    Object.assign(overlayRoot.style, {
      position: "absolute",
      top: "0",
      left: "0",
      pointerEvents: "none",
      width: "100%",
      height: "100%",
      zIndex: "10",
      userSelect: "none",
    });
    viewer.container.appendChild(overlayRoot);

    // basic styles
    const styleTag = document.createElement("style");
    styleTag.textContent = `
      .overlay-panel input{color:#fff;background:transparent;border:1px solid rgba(255,255,255,0.4);padding:4px;border-radius:4px;}
      .overlay-panel input::placeholder{color:#fff;opacity:0.7;}
      .overlay-panel button{background:#18a058;color:#fff;border:none;padding:6px 10px;border-radius:4px;cursor:pointer;}
      .overlay-panel span{align-self:center;color:#fff;}
    `;
    overlayRoot.appendChild(styleTag);

    const panelStyle = (el) => {
      el.className = "overlay-panel";
      Object.assign(el.style, {
        position: "absolute",
        pointerEvents: "auto",
        background: "rgba(0,0,0,0.55)",
        color: "#fff",
        padding: "6px",
        borderRadius: "6px",
        backdropFilter: "blur(2px)",
        display: "grid",
        gridTemplateColumns: "auto auto",
        gap: "4px",
        maxWidth: "240px",
      });
    };

    // left box (text)
    const leftBox = document.createElement("div");
    panelStyle(leftBox);
    Object.assign(leftBox.style, { transformOrigin: "center center", willChange: "transform", backfaceVisibility: "hidden" });
    const textInput = document.createElement("input");
    textInput.type = "text";
    textInput.placeholder = "label";
    textInput.style.gridColumn = "1 / span 2";
    textInput.style.width = "200px";
    leftBox.append(textInput);
    overlayRoot.appendChild(leftBox);

    // right box (zoom/size + buttons)
    const rightBox = document.createElement("div");
    panelStyle(rightBox);

    const zoomLbl = document.createElement("span");
    zoomLbl.textContent = "zoom:";
    const zoomInput = document.createElement("input");
    zoomInput.type = "number";
    zoomInput.value = currentZoomAtCenter().toFixed(2);
    zoomInput.min = "0";
    zoomInput.max = "24";
    zoomInput.step = "0.01";
    zoomInput.style.width = "80px";

    const sizeLbl = document.createElement("span");
    sizeLbl.textContent = "size:";
    const sizeInput = document.createElement("input");
    sizeInput.type = "number";
    sizeInput.value = "10";
    sizeInput.min = "1";
    sizeInput.step = "1";
    sizeInput.style.width = "80px";

    const btnRow = document.createElement("div");
    Object.assign(btnRow.style, { gridColumn: "1 / span 2", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px" });
    const saveBtn = document.createElement("button");
    saveBtn.textContent = "Save";
    const cancelBtn = document.createElement("button");
    cancelBtn.textContent = "Cancel";
    cancelBtn.style.background = "#d03050";
    btnRow.append(saveBtn, cancelBtn);
    rightBox.append(zoomLbl, zoomInput, sizeLbl, sizeInput, btnRow);
    overlayRoot.appendChild(rightBox);

    // rotation handle
    const rotBtn = document.createElement("div");
    Object.assign(rotBtn.style, {
      position: "absolute",
      pointerEvents: "auto",
      userSelect: "none",
      background: "rgba(255,255,255,0.95)",
      border: "1px solid #888",
      borderRadius: "14px",
      width: "28px",
      height: "28px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "grab",
      touchAction: "none",
    });
    rotBtn.title = "Rotate";
    rotBtn.textContent = "⟳";
    overlayRoot.appendChild(rotBtn);

    // anchor UI
    // replace existing postRenderFn with this
    postRenderFn = () => {
      // compute geographic anchors
      const SW = cornersLL[0],
        SEc = cornersLL[1],
        NE = cornersLL[2],
        NW = cornersLL[3];
      const midLeft = [(SW[0] + NW[0]) / 2, (SW[1] + NW[1]) / 2];
      const midRight = [(SEc[0] + NE[0]) / 2, (SEc[1] + NE[1]) / 2];
      const leftAnchorLL = [(midLeft[0] + centerLL[0]) * 0.5, (midLeft[1] + centerLL[1]) * 0.5];
      const rightAnchorLL = [(midRight[0] + centerLL[0]) * 0.5, (midRight[1] + centerLL[1]) * 0.5];
      const rotAnchorLL = [SEc[0], SEc[1]];

      // convert to window coordinates
      const pLeft = toWin(leftAnchorLL[0], leftAnchorLL[1]);
      const pRight = toWin(rightAnchorLL[0], rightAnchorLL[1]);
      const pRot = toWin(rotAnchorLL[0], rotAnchorLL[1]);

      // set left box (label) position and rotate it
      if (pLeft) {
        leftBox.style.left = `${pLeft.x}px`;
        leftBox.style.top = `${pLeft.y}px`;
        leftBox.style.transform = `translate(-50%, -50%) rotate(${-angleDeg}deg)`;
      }

      // set right box (controls) initial position (centered)
      if (pRight) {
        rightBox.style.left = `${pRight.x}px`;
        rightBox.style.top = `${pRight.y}px`;
        rightBox.style.transform = `translate(-50%, -50%)`;
      }

      // rotation handle
      if (pRot) {
        rotBtn.style.left = `${pRot.x - 14}px`;
        rotBtn.style.top = `${pRot.y - 14}px`;
      }

      // helper to detect intersection
      const rectsIntersect = (a, b) => !(a.right < b.left || a.left > b.right || a.bottom < b.top || a.top > b.bottom);

      // avoid overlap: if boxes intersect, move rightBox under leftBox
      try {
        const lb = leftBox.getBoundingClientRect();
        const rb = rightBox.getBoundingClientRect();
        const PAD = 8; // pixel gap

        if (lb.width && rb.width && rectsIntersect(lb, rb)) {
          // place rightBox below leftBox with a small padding
          // align horizontally to leftBox center
          rightBox.style.left = `${pLeft.x}px`;
          rightBox.style.top = `${pLeft.y + lb.height / 2 + rb.height / 2 + PAD}px`;
          // adjust transform so it is aligned horizontally and sits below
          rightBox.style.transform = `translate(-50%, 0)`;
        }
      } catch (err) {
        // ignore measurement errors
      }
    };

    viewer.scene.postRender.addEventListener(postRenderFn);
    postRenderFn();

    // helpers for rotation pointer math
    const centerWin = () => {
      const c = SceneTransforms?.wgs84ToWindowCoordinates
        ? SceneTransforms.wgs84ToWindowCoordinates(viewer.scene, Cartesian3.fromDegrees(centerLL[0], centerLL[1]))
        : viewer.scene.cartesianToCanvasCoordinates(Cartesian3.fromDegrees(centerLL[0], centerLL[1]));
      return c || new Cartesian2(0, 0);
    };

    // pointer events
    const startRotate = (e) => {
      e.preventDefault();
      e.stopPropagation();
      rotating = true;
      pointerId = e.pointerId;
      captureEl = rotBtn;
      cameraLocked = true;
      disableCameraControls(viewer, originalCameraControls);
      viewer.container.style.cursor = "grabbing";
      try {
        rotBtn.setPointerCapture(pointerId);
      } catch {}
      const c = centerWin();
      const pSE = toWin(cornersLL[1][0], cornersLL[1][1]) || c;
      baseAngleRad = Math.atan2(-(pSE.y - c.y), pSE.x - c.x);
      escHandler = (ev) => {
        if (ev.key === "Escape") {
          angleDeg = angleOffsetDeg;
          viewer.scene.requestRender();
          endRotateSession();
        }
      };
      document.addEventListener("keydown", escHandler, true);
    };
    const moveRotate = (e) => {
      if (!rotating || e.pointerId !== pointerId) return;
      const c = centerWin();
      const currRad = Math.atan2(-(e.clientY - c.y), e.clientX - c.x);
      const deltaRad = currRad - baseAngleRad;
      angleDeg = angleOffsetDeg + CesiumMath.toDegrees(deltaRad);
      rotateCorners(angleDeg);
      viewer.scene.requestRender();
    };
    const finishRotate = (e) => {
      if (e && pointerId != null && e.pointerId !== pointerId) return;
      endRotateSession();
    };

    rotBtn.addEventListener("pointerdown", startRotate, { passive: false });
    rotBtn.addEventListener("pointermove", moveRotate, { passive: true });
    rotBtn.addEventListener("pointerup", finishRotate, { passive: true });
    rotBtn.addEventListener("pointercancel", finishRotate, { passive: true });
    rotBtn.addEventListener("lostpointercapture", finishRotate, { passive: true });

    // Save
    saveBtn.onclick = (e) => {
      e.stopPropagation();
      const ring = cornersLL.map(([lo, la]) => [lo, la]);
      const first = ring[0],
        last = ring[ring.length - 1];
      if (first[0] !== last[0] || first[1] !== last[1]) ring.push([first[0], first[1]]);

      const zRaw = Number(zoomInput.value);
      const zoomVal = Number.isFinite(zRaw) ? Math.round(zRaw * 100) / 100 : 0;

      const sizeRaw = Number(sizeInput.value);
      const sizeVal = Number.isFinite(sizeRaw) ? sizeRaw : 10;

      const feature = {
        type: "Feature",
        geometry: { type: "Polygon", coordinates: [ring] },
        properties: {
          text: textInput.value || "",
          zoom: zoomVal, // 2 decimals
          size: sizeVal,
          rotationDeg: angleDeg,
          center: { lon: centerLL[0], lat: centerLL[1] },
          bbox: [bounds.west, bounds.south, bounds.east, bounds.north],
          id: createUniqueId(),
        },
      };

      consumed = true;
      if (typeof onSave === "function") onSave(feature);
      handler.destroy();
    };

    // Cancel
    const resetForRedraw = () => {
      endRotateSession();
      detachPostRender();
      rmOverlay();
      rmAllEntities();
      isDrawing = false;
      startPoint = null;
      currentMouse = null;
      angleDeg = 0;
      angleOffsetDeg = 0;
      baseAngleRad = 0;
      consumed = false;
      if (cameraLocked) {
        restoreCameraControls(viewer, originalCameraControls);
        cameraLocked = false;
      }
      handler.setInputAction(onLeftDown, ScreenSpaceEventType.LEFT_DOWN);
      viewer.scene.requestRender();
    };
    cancelBtn.onclick = (e) => {
      e.stopPropagation();
      resetForRedraw();
    };

    // end draw phase
    isDrawing = false;
    restoreCameraControls(viewer, originalCameraControls);
  };

  // wire inputs
  handler.setInputAction(onLeftDown, ScreenSpaceEventType.LEFT_DOWN);
  handler.setInputAction(onMouseMove, ScreenSpaceEventType.MOUSE_MOVE);
  handler.setInputAction(onLeftUp, ScreenSpaceEventType.LEFT_UP);

  return handler; // call .destroy() to teardown
}

export function openOverlayTextEditorUI(viewer, initialFeature, onSave, originalCameraControls) {
  // read props
  const p = (initialFeature && initialFeature.properties) || {};
  const text0 = String(p.text || "");
  const zoom0 = Number.isFinite(+p.zoom) ? Math.round(+p.zoom * 100) / 100 : 0;
  const size0 = Number.isFinite(+p.size) ? +p.size : 10;
  const rot0 = Number.isFinite(+p.rotationDeg) ? +p.rotationDeg : 0;

  // bounds + center
  const bbox =
    Array.isArray(p.bbox) && p.bbox.length === 4
      ? { west: p.bbox[0], south: p.bbox[1], east: p.bbox[2], north: p.bbox[3] }
      : (() => {
          const c = p.center || { lon: 0, lat: 0 };
          const pad = 0.001;
          return { west: c.lon - pad, south: c.lat - pad, east: c.lon + pad, north: c.lat + pad };
        })();

  // state
  let overlayRoot = null;
  let postRenderFn = null;
  let rotating = false;
  let pointerId = null;
  let angleDeg = rot0;
  let angleOffsetDeg = rot0;
  let baseAngleRad = 0;
  let escHandler = null;
  let captureEl = null;

  // drag state
  let dragging = false;
  let dragPid = null;
  let dragInv = null; // ENU^-1 at center
  let dragEnu = null; // ENU at center
  let dragLastLocal = null; // last pointer in local meters
  let savedBaseCorners = null;
  let savedCenterLL = null;

  // entities ids
  const OUTLINE_ID = "rect-outline-ui-edit";
  const POLY_ID = "rect-rot-poly-edit";

  const handler = new ScreenSpaceEventHandler(viewer.canvas);
  const originalDestroy = handler.destroy.bind(handler);

  // setup entities
  const baseCorners = [
    [bbox.west, bbox.south],
    [bbox.east, bbox.south],
    [bbox.east, bbox.north],
    [bbox.west, bbox.north],
    [bbox.west, bbox.south],
  ];
  const centerLL = [(bbox.west + bbox.east) / 2, (bbox.south + bbox.north) / 2];
  const cornersLL = baseCorners.map((p) => [...p]);

  // dashed outline entity (visible)
  const borderEntity = viewer.entities.add({
    id: OUTLINE_ID,
    polyline: {
      positions: new CallbackProperty(() => {
        const flat = [cornersLL[0][0], cornersLL[0][1], cornersLL[1][0], cornersLL[1][1], cornersLL[2][0], cornersLL[2][1], cornersLL[3][0], cornersLL[3][1], cornersLL[0][0], cornersLL[0][1]];
        return Cartesian3.fromDegreesArray(flat);
      }, false),
      width: 1,
      material: new PolylineDashMaterialProperty({ color: Color.RED, gapColor: Color.TRANSPARENT, dashLength: 12 }),
      clampToGround: false,
    },
  });

  // hidden polygon (math only)
  const polyEntity = viewer.entities.add({
    id: POLY_ID,
    polygon: {
      hierarchy: new CallbackProperty(() => {
        const pos = Cartesian3.fromDegreesArray(cornersLL.flat());
        return new PolygonHierarchy(pos);
      }, false),
      material: Color.TRANSPARENT,
      outline: false,
      perPositionHeight: false,
    },
  });

  // rotate corners helper
  const rotateCorners = (deg) => {
    const rad = CesiumMath.toRadians(deg);
    const centerCart = Cartesian3.fromDegrees(centerLL[0], centerLL[1]);
    const enu = Transforms.eastNorthUpToFixedFrame(centerCart);
    const enuInv = Matrix4.inverse(enu, new Matrix4());
    const Rz = Matrix3.fromRotationZ(rad);
    const Rz4 = Matrix4.fromRotationTranslation(Rz);

    const apply = (lon, lat) => {
      const pW = Cartesian3.fromDegrees(lon, lat);
      const pL = Matrix4.multiplyByPoint(enuInv, pW, new Cartesian3());
      const pRL = Matrix4.multiplyByPoint(Rz4, pL, new Cartesian3());
      const pWW = Matrix4.multiplyByPoint(enu, pRL, new Cartesian3());
      const cg = Cartographic.fromCartesian(pWW);
      return [CesiumMath.toDegrees(cg.longitude), CesiumMath.toDegrees(cg.latitude)];
    };
    const rotated = baseCorners.map(([lo, la]) => apply(lo, la));
    cornersLL.splice(0, cornersLL.length, ...rotated);
    viewer.scene.requestRender();
  };

  // translate helper (meters in local ENU)
  const translateByMeters = (dx, dy) => {
    if (!dragEnu || !dragInv) {
      const cW = Cartesian3.fromDegrees(centerLL[0], centerLL[1]);
      dragEnu = Transforms.eastNorthUpToFixedFrame(cW);
      dragInv = Matrix4.inverse(dragEnu, new Matrix4());
    }
    // move all base corners
    for (let i = 0; i < baseCorners.length; i++) {
      const [lo, la] = baseCorners[i];
      const w = Cartesian3.fromDegrees(lo, la);
      const l = Matrix4.multiplyByPoint(dragInv, w, new Cartesian3());
      l.x += dx;
      l.y += dy; // apply shift
      const w2 = Matrix4.multiplyByPoint(dragEnu, l, new Cartesian3());
      const cg = Cartographic.fromCartesian(w2);
      baseCorners[i] = [CesiumMath.toDegrees(cg.longitude), CesiumMath.toDegrees(cg.latitude)];
    }
    // move center
    {
      const cW = Cartesian3.fromDegrees(centerLL[0], centerLL[1]);
      const cL = Matrix4.multiplyByPoint(dragInv, cW, new Cartesian3());
      cL.x += dx;
      cL.y += dy;
      const cW2 = Matrix4.multiplyByPoint(dragEnu, cL, new Cartesian3());
      const cg = Cartographic.fromCartesian(cW2);
      centerLL[0] = CesiumMath.toDegrees(cg.longitude);
      centerLL[1] = CesiumMath.toDegrees(cg.latitude);
    }
    // rebuild visible corners with same rotation
    rotateCorners(angleDeg);
  };

  // overlay + UI
  overlayRoot = document.createElement("div");
  Object.assign(overlayRoot.style, {
    position: "absolute",
    top: "0",
    left: "0",
    pointerEvents: "none",
    width: "100%",
    height: "100%",
    zIndex: "10",
    userSelect: "none",
  });
  viewer.container.appendChild(overlayRoot);

  const styleTag = document.createElement("style");
  styleTag.textContent = `
    .overlay-panel input{color:#fff;background:transparent;border:1px solid rgba(255,255,255,0.4);padding:4px;border-radius:4px;}
    .overlay-panel input::placeholder{color:#fff;opacity:0.7;}
    .overlay-panel button{background:#18a058;color:#fff;border:none;padding:6px 10px;border-radius:4px;cursor:pointer;}
    .overlay-panel span{align-self:center;color:#fff;}
  `;
  overlayRoot.appendChild(styleTag);

  const panelStyle = (el) => {
    el.className = "overlay-panel";
    Object.assign(el.style, {
      position: "absolute",
      pointerEvents: "auto",
      background: "rgba(0,0,0,0.55)",
      color: "#fff",
      padding: "6px",
      borderRadius: "6px",
      backdropFilter: "blur(2px)",
      display: "grid",
      gridTemplateColumns: "auto auto",
      gap: "4px",
      maxWidth: "240px",
    });
  };

  // left box
  const leftBox = document.createElement("div");
  panelStyle(leftBox);
  Object.assign(leftBox.style, { transformOrigin: "center center", willChange: "transform", backfaceVisibility: "hidden" });
  const textInput = document.createElement("input");
  textInput.type = "text";
  textInput.placeholder = "label";
  textInput.style.gridColumn = "1 / span 2";
  textInput.style.width = "200px";
  textInput.value = text0; // prefill text
  leftBox.append(textInput);
  overlayRoot.appendChild(leftBox);

  // right box
  const rightBox = document.createElement("div");
  panelStyle(rightBox);
  const zoomLbl = document.createElement("span");
  zoomLbl.textContent = "zoom:";
  const zoomInput = document.createElement("input");
  zoomInput.type = "number";
  zoomInput.min = "0";
  zoomInput.max = "24";
  zoomInput.step = "0.01";
  zoomInput.style.width = "80px";
  zoomInput.value = String(zoom0); // prefill zoom
  const sizeLbl = document.createElement("span");
  sizeLbl.textContent = "size:";
  const sizeInput = document.createElement("input");
  sizeInput.type = "number";
  sizeInput.min = "1";
  sizeInput.step = "1";
  sizeInput.style.width = "80px";
  sizeInput.value = String(size0); // prefill size
  const btnRow = document.createElement("div");
  Object.assign(btnRow.style, { gridColumn: "1 / span 2", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px" });
  const saveBtn = document.createElement("button");
  saveBtn.textContent = "Save";
  const cancelBtn = document.createElement("button");
  cancelBtn.textContent = "Cancel";
  cancelBtn.style.background = "#d03050";
  btnRow.append(saveBtn, cancelBtn);
  rightBox.append(zoomLbl, zoomInput, sizeLbl, sizeInput, btnRow);
  overlayRoot.appendChild(rightBox);

  // rotation handle
  const rotBtn = document.createElement("div");
  Object.assign(rotBtn.style, {
    position: "absolute",
    pointerEvents: "auto",
    userSelect: "none",
    background: "rgba(255,255,255,0.95)",
    border: "1px solid #888",
    borderRadius: "14px",
    width: "28px",
    height: "28px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "grab",
    touchAction: "none",
  });
  rotBtn.title = "Rotate";
  rotBtn.textContent = "⟳";
  overlayRoot.appendChild(rotBtn);

  // anchor UI
  const toWin = (lon, lat) => {
    const s = viewer.scene;
    return SceneTransforms?.wgs84ToWindowCoordinates ? SceneTransforms.wgs84ToWindowCoordinates(s, Cartesian3.fromDegrees(lon, lat)) : s.cartesianToCanvasCoordinates(Cartesian3.fromDegrees(lon, lat));
  };
  // replace postRenderFn with this
  postRenderFn = () => {
    // compute anchors
    const SW = cornersLL[0],
      SEc = cornersLL[1],
      NE = cornersLL[2],
      NW = cornersLL[3];
    const midLeft = [(SW[0] + NW[0]) / 2, (SW[1] + NW[1]) / 2];
    const midRight = [(SEc[0] + NE[0]) / 2, (SEc[1] + NE[1]) / 2];
    const leftAnchorLL = [(midLeft[0] + centerLL[0]) * 0.5, (midLeft[1] + centerLL[1]) * 0.5];
    const rightAnchorLL = [(midRight[0] + centerLL[0]) * 0.5, (midRight[1] + centerLL[1]) * 0.5];
    const rotAnchorLL = [SEc[0], SEc[1]];

    // window coords
    const pLeft = toWin(leftAnchorLL[0], leftAnchorLL[1]);
    const pRight = toWin(rightAnchorLL[0], rightAnchorLL[1]);
    const pRot = toWin(rotAnchorLL[0], rotAnchorLL[1]);

    // position left (label)
    if (pLeft) {
      leftBox.style.left = `${pLeft.x}px`;
      leftBox.style.top = `${pLeft.y}px`;
      leftBox.style.transform = `translate(-50%, -50%) rotate(${-angleDeg}deg)`;
    }

    // position right (controls) default
    if (pRight) {
      rightBox.style.left = `${pRight.x}px`;
      rightBox.style.top = `${pRight.y}px`;
      rightBox.style.transform = `translate(-50%, -50%)`;
    }

    // rotation handle
    if (pRot) {
      rotBtn.style.left = `${pRot.x - 14}px`;
      rotBtn.style.top = `${pRot.y - 14}px`;
    }

    // overlap avoidance and viewport clamp
    try {
      const PAD = 8;
      const lb = leftBox.getBoundingClientRect();
      const rb = rightBox.getBoundingClientRect();
      const containerRect = viewer.container.getBoundingClientRect();

      // basic intersection test
      const rectsIntersect = (a, b) => !(a.right < b.left || a.left > b.right || a.bottom < b.top || a.top > b.bottom);

      if (lb.width && rb.width && rectsIntersect(lb, rb)) {
        // prefer placing rightBox below leftBox
        const newLeft = pLeft ? pLeft.x : rb.left + rb.width / 2;
        const newTop = pLeft ? pLeft.y + lb.height / 2 + rb.height / 2 + PAD : rb.top + lb.height + PAD;
        rightBox.style.left = `${newLeft}px`;
        rightBox.style.top = `${newTop}px`;
        rightBox.style.transform = `translate(-50%, 0)`; // align under
      }

      // clamp rightBox inside viewer container if it escapes
      const rb2 = rightBox.getBoundingClientRect();
      let shiftX = 0;
      let shiftY = 0;
      if (rb2.left < containerRect.left + PAD) shiftX = containerRect.left + PAD - rb2.left;
      if (rb2.right > containerRect.right - PAD) shiftX = containerRect.right - PAD - rb2.right;
      if (rb2.top < containerRect.top + PAD) shiftY = containerRect.top + PAD - rb2.top;
      if (rb2.bottom > containerRect.bottom - PAD) shiftY = containerRect.bottom - PAD - rb2.bottom;

      if (shiftX !== 0 || shiftY !== 0) {
        // parse current numeric px and apply shift
        const curLeft = parseFloat(rightBox.style.left || "0");
        const curTop = parseFloat(rightBox.style.top || "0");
        rightBox.style.left = `${curLeft + shiftX}px`;
        rightBox.style.top = `${curTop + shiftY}px`;
        // keep transform as-is (usually translate(-50%, ...))
      }
    } catch (err) {
      // ignore DOM measurement errors
    }
  };

  viewer.scene.postRender.addEventListener(postRenderFn);

  // rotation math
  const centerWin = () => {
    const s = viewer.scene;
    return SceneTransforms?.wgs84ToWindowCoordinates
      ? SceneTransforms.wgs84ToWindowCoordinates(s, Cartesian3.fromDegrees(centerLL[0], centerLL[1]))
      : s.cartesianToCanvasCoordinates(Cartesian3.fromDegrees(centerLL[0], centerLL[1]));
  };
  const startRotate = (e) => {
    e.preventDefault();
    e.stopPropagation();
    rotating = true;
    pointerId = e.pointerId;
    captureEl = rotBtn;
    try {
      rotBtn.setPointerCapture(pointerId);
    } catch {}
    const c = centerWin();
    const pSE = toWin(cornersLL[1][0], cornersLL[1][1]) || c;
    baseAngleRad = Math.atan2(-(pSE.y - c.y), pSE.x - c.x);
    escHandler = (ev) => {
      if (ev.key === "Escape") {
        angleDeg = angleOffsetDeg;
        viewer.scene.requestRender();
        endRotateSession();
      }
    };
    document.addEventListener("keydown", escHandler, true);
  };
  const moveRotate = (e) => {
    if (!rotating || e.pointerId !== pointerId) return;
    const c = centerWin();
    const currRad = Math.atan2(-(e.clientY - c.y), e.clientX - c.x);
    const deltaRad = currRad - baseAngleRad;
    angleDeg = angleOffsetDeg + CesiumMath.toDegrees(deltaRad);
    rotateCorners(angleDeg);
    viewer.scene.requestRender();
  };
  const finishRotate = (e) => {
    if (e && pointerId != null && e.pointerId !== pointerId) return;
    endRotateSession();
  };
  const endRotateSession = () => {
    if (!rotating) return;
    rotating = false;
    angleOffsetDeg = angleDeg;
    try {
      if (captureEl && pointerId != null) captureEl.releasePointerCapture(pointerId);
    } catch {}
    pointerId = null;
    captureEl = null;
    if (escHandler) {
      document.removeEventListener("keydown", escHandler, true);
      escHandler = null;
    }
  };

  rotBtn.addEventListener("pointerdown", startRotate, { passive: false });
  rotBtn.addEventListener("pointermove", moveRotate, { passive: true });
  rotBtn.addEventListener("pointerup", finishRotate, { passive: true });
  rotBtn.addEventListener("pointercancel", finishRotate, { passive: true });
  rotBtn.addEventListener("lostpointercapture", finishRotate, { passive: true });

  // apply initial rotation
  rotateCorners(angleDeg);

  // --------- DRAG TO MOVE (polyline/polygon) ----------
  const getWinCartesian = (pos) => {
    // project screen pos to globe
    if (!pos) return null;
    const s = viewer.scene;
    const c1 = s.pickPositionSupported ? s.pickPosition(pos) : null;
    if (c1) return c1;
    const c2 = viewer.camera.pickEllipsoid(pos);
    if (c2) return c2;
    if (s.globe) {
      const ray = viewer.camera.getPickRay(pos);
      if (ray) return s.globe.pick(ray, s);
    }
    return null;
  };
  const toLocal = (w) => Matrix4.multiplyByPoint(dragInv, w, new Cartesian3());

  const dragEscCancel = (ev) => {
    if (ev.key !== "Escape") return;
    // revert to saved position
    if (savedBaseCorners) {
      for (let i = 0; i < baseCorners.length; i++) baseCorners[i] = [...savedBaseCorners[i]];
      centerLL[0] = savedCenterLL[0];
      centerLL[1] = savedCenterLL[1];
      rotateCorners(angleDeg);
      viewer.scene.requestRender();
    }
    stopDragging();
  };

  const startDragging = (movement) => {
    const picked = viewer.scene.pick(movement.position);
    if (!picked) return;
    const id = picked?.id?.id || picked?.id;
    if (id !== OUTLINE_ID && id !== POLY_ID) return;

    // lock camera
    try {
      disableCameraControls(viewer, originalCameraControls);
    } catch {}

    dragging = true;
    dragPid = null;
    // setup ENU at center
    const cW = Cartesian3.fromDegrees(centerLL[0], centerLL[1]);
    dragEnu = Transforms.eastNorthUpToFixedFrame(cW);
    dragInv = Matrix4.inverse(dragEnu, new Matrix4());
    // save for cancel
    savedBaseCorners = baseCorners.map(([lo, la]) => [lo, la]);
    savedCenterLL = [centerLL[0], centerLL[1]];

    const first = getWinCartesian(movement.position);
    if (!first) {
      stopDragging();
      return;
    }
    dragLastLocal = toLocal(first);
    document.addEventListener("keydown", dragEscCancel, true);
  };

  const moveDragging = (movement) => {
    if (!dragging) return;
    const curW = getWinCartesian(movement.endPosition || movement.startPosition || movement);
    if (!curW) return;
    const curL = toLocal(curW);
    const dx = curL.x - dragLastLocal.x;
    const dy = curL.y - dragLastLocal.y;
    if (Number.isFinite(dx) && Number.isFinite(dy)) translateByMeters(dx, dy);
    dragLastLocal = curL;
  };

  const stopDragging = () => {
    if (!dragging) return;
    dragging = false;
    dragLastLocal = null;
    dragInv = null;
    dragEnu = null;
    savedBaseCorners = null;
    savedCenterLL = null;
    try {
      restoreCameraControls(viewer, originalCameraControls);
    } catch {}
    try {
      document.removeEventListener("keydown", dragEscCancel, true);
    } catch {}
  };

  handler.setInputAction(startDragging, ScreenSpaceEventType.LEFT_DOWN);
  handler.setInputAction(moveDragging, ScreenSpaceEventType.MOUSE_MOVE);
  handler.setInputAction(stopDragging, ScreenSpaceEventType.LEFT_UP);
  handler.setInputAction(stopDragging, ScreenSpaceEventType.RIGHT_DOWN);

  // Compute bbox from current corners
  const computeBBoxFromCorners = (cornersLL) => {
    // use first 4 unique corners
    let west = Infinity,
      south = Infinity,
      east = -Infinity,
      north = -Infinity;
    for (let i = 0; i < 4; i++) {
      const [lon, lat] = cornersLL[i];
      if (lon < west) west = lon;
      if (lon > east) east = lon;
      if (lat < south) south = lat;
      if (lat > north) north = lat;
    }
    return [west, south, east, north];
  };

  // Save -> emit updated feature
  saveBtn.onclick = (e) => {
    e.stopPropagation();

    // close ring
    const ring = cornersLL.map(([lo, la]) => [lo, la]);
    const first = ring[0],
      last = ring[ring.length - 1];
    if (first[0] !== last[0] || first[1] !== last[1]) ring.push([first[0], first[1]]);

    // recompute bbox from current corners
    const newBBox = computeBBoxFromCorners(cornersLL);

    const zRaw = Number(zoomInput.value);
    const zoomVal = Number.isFinite(zRaw) ? Math.round(zRaw * 100) / 100 : 0;

    const sizeRaw = Number(sizeInput.value);
    const sizeVal = Number.isFinite(sizeRaw) ? sizeRaw : 10;

    const out = {
      type: "Feature",
      geometry: { type: "Polygon", coordinates: [ring] },
      properties: {
        text: textInput.value || "",
        zoom: zoomVal, // 2 decimals
        size: sizeVal,
        rotationDeg: angleDeg,
        center: { lon: centerLL[0], lat: centerLL[1] },
        bbox: newBBox,
        id: initialFeature.properties.id,
      },
    };


    if (typeof onSave === "function") onSave(out);
    handler.destroy();
  };

  // Cancel
  cancelBtn.onclick = (e) => {
    e.stopPropagation();
    handler.destroy();
  };

  // cleanup override
  const detachPostRender2 = () => {
    if (postRenderFn) viewer.scene.postRender.removeEventListener(postRenderFn);
    postRenderFn = null;
  };
  const rmOverlay2 = () => {
    try {
      overlayRoot?.remove();
    } catch {}
    overlayRoot = null;
  };
  const cleanup = () => {
    stopDragging();
    detachPostRender2();
    rmOverlay2();
    try {
      viewer.entities.remove(borderEntity);
    } catch {}
    try {
      viewer.entities.remove(polyEntity);
    } catch {}
    if (escHandler) {
      document.removeEventListener("keydown", escHandler, true);
      escHandler = null;
    }
  };
  handler.destroy = () => {
    cleanup();
    originalDestroy();
  };
}
